import React, { useEffect, useMemo, useState } from 'react';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { inject } from 'mobx-react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import type { MetricsStore } from './types';

type StoreCollection = string | { store: string; force?: boolean };

type ExtractStoreNames<T extends readonly StoreCollection[]> = T[number] extends infer U
  ? U extends string
    ? U extends `${infer StoreName}.${string}`
      ? StoreName
      : U
    : U extends { store: string }
      ? U['store'] extends `${infer StoreName}.${string}`
        ? StoreName
        : U['store']
      : never
  : never;

/**
 * Maps store names to their actual types.
 * Add new store types here as they become available.
 */
type StoreTypeMap = {
  $metrics: MetricsStore;
};

export type InjectedStoreProps<T extends readonly StoreCollection[]> = {
  [K in ExtractStoreNames<T>]: K extends keyof StoreTypeMap ? StoreTypeMap[K] : unknown;
} & {
  $store: Record<string, unknown>;
  loading: boolean;
};

type CancelableRequest = {
  promise: Promise<unknown[]>;
  cancel: () => void;
};

export default function storeLoader<const T extends readonly StoreCollection[]>(...collections: T) {
  return <P extends InjectedStoreProps<T>>(Component: React.ComponentType<P>) => {
    const Wrapper = inject('$store')(
      ({
        $store,
        ...props
      }: Omit<P, ExtractStoreNames<T> | '$store' | 'loading'> & { $store: Record<string, unknown> }) => {
        const [loading, setLoading] = useState(true);

        const stores = useMemo(
          () =>
            collections.map((collection) => {
              let force = false;
              let collectionStr: string;

              if (typeof collection === 'string') {
                collectionStr = collection;
              } else {
                force = collection.force ?? false;
                collectionStr = collection.store;
              }

              const [storeName, collectionName = 'collection'] = collectionStr.split('.');

              return {
                store: $store[storeName],
                storeName,
                collection: ($store[storeName] as Record<string, unknown>)?.[collectionName],
                force
              };
            }),
          [$store]
        );

        const [request, setRequest] = useState<CancelableRequest | null>(null);

        useEffect(() => {
          setRequest(
            makeCancelable(
              Promise.all(
                stores.map(({ collection, force }) => {
                  const fetchable = collection as { fetch?: (opts: { force: boolean }) => Promise<unknown> };
                  return fetchable?.fetch?.({ force });
                })
              )
            )
          );
        }, [stores]);

        useEffect(() => {
          request?.promise
            .then(() => {
              setRequest(null);
              setLoading(false);
            })
            .catch((err: unknown) => {
              if (!(err instanceof CanceledError)) {
                setLoading(false);
                console.error('Error loading required resource', err);
              }
            });
          return () => {
            if (request) {
              request.cancel();
            }
          };
        }, [request]);

        return (
          <Component
            {...(props as unknown as P)}
            {...({ $store, loading } as unknown as P)}
            {...(stores.reduce(
              (acc, { storeName, store }) => {
                acc[storeName] = store;
                return acc;
              },
              {} as Record<string, unknown>
            ) as unknown as P)}
          />
        );
      }
    ) as unknown as React.ComponentType<Omit<P, ExtractStoreNames<T> | '$store' | 'loading'>>;

    hoistNonReactStatics(Wrapper, Component);
    return Wrapper;
  };
}
